import React, { useReducer, useContext } from 'react';
import { PackAnalyticsType } from 'types/enums';
import { AssetCategoryGroup } from 'utils/analytics/AnalyticsV2Service.generated';
import { getPackAnalyticsType, getPackAnalyticsTypeV2 } from 'helpers';
import { analytics } from '../../utils/analytics';
import {
    TrackingContextType,
    SampleCollectionInitializedTracker,
    PackCardPlayButtonClickTrackerType,
    PackPlayButtonClickTrackerV2Type,
} from './TrackingContext.types';
import { trackingReducer } from './TrackingContext.reducer';
import {
    defaultTrackingContext,
    defaultTrackingState,
} from './TrackingContext.default';

interface TrackingProviderProps {
    children: React.ReactNode;
}

export const TrackingContext = React.createContext<TrackingContextType>(
    defaultTrackingContext,
);

export const useTrackingContext = () => useContext(TrackingContext);

export const TrackingProvider = ({
    children,
}: TrackingProviderProps): JSX.Element => {
    // REDUCER
    const [state, trackingDispatch] = useReducer(
        trackingReducer,
        defaultTrackingState,
    );

    const getPackCardPlayButtonClickTracker: PackCardPlayButtonClickTrackerType =
        ({ item, carouselName, carouselId, page, position }) => {
            const packAnalyticsType = getPackAnalyticsType(item);

            const Tracker = (): void => {
                let pack = item;

                const trackingDispatchType =
                    packAnalyticsType === PackAnalyticsType.Pack
                        ? 'packPlayed'
                        : 'rePackPlayed';

                if (item.isFree) {
                    pack = { ...item, name: `Free ${item.name}` };
                }

                trackingDispatch({
                    type: trackingDispatchType,
                    payload: {
                        item: pack,
                        source: {
                            interaction: `${packAnalyticsType} Card`,
                            carouselId: carouselId,
                            carouselName: carouselName,
                            page: page,
                            position: position,
                        },
                    },
                });
            };

            return Tracker;
        };

    const getSampleCollectionInitializedTracker: SampleCollectionInitializedTracker =
        ({
            samples,
            interaction,
            carouselName,
            carouselId,
            page,
            position,
        }) => {
            const Tracker = (): void => {
                trackingDispatch({
                    type: 'sampleCollectionInitialized',
                    payload: {
                        samples,
                        source: {
                            interaction: interaction,
                            carouselId: carouselId,
                            carouselName: carouselName,
                            page: page,
                            position: position,
                        },
                    },
                });
            };

            return Tracker;
        };

    const getPackPlayButtonClickTrackerV2: PackPlayButtonClickTrackerV2Type = ({
        item,
        interactionSource,
        contentGroup,
        contentList,
    }) => {
        const assetCategory = getPackAnalyticsTypeV2(item);

        const Tracker = (totalTime: number): void => {
            if (assetCategory) {
                analytics.trackAssetPlayed({
                    assetCategory,
                    assetId: item.guid,
                    interactionSource,
                    assetCategoryGroup: AssetCategoryGroup.Audio,
                    assetLength: totalTime,
                    assetName: item.name,
                    ctaContentGroup: contentGroup,
                    ctaContentList: contentList,
                });
            }
        };

        return Tracker;
    };

    return (
        <TrackingContext.Provider
            value={{
                state: { ...state },
                trackingDispatch,
                getSampleCollectionInitializedTracker,
                getPackCardPlayButtonClickTracker,
                getPackPlayButtonClickTrackerV2,
            }}
        >
            {children}
        </TrackingContext.Provider>
    );
};

export default TrackingProvider;
